/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import { Box } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
  selectFailedToParseFlightLegs,
  selectMissionPlanById,
  selectSuccessfullyParsedFlightLegs,
  selectUserId,
} from '../../../store';
import {
  getFlightLegsInfo,
  parseFlightLegFile,
  updateFailedToParseFlightLegs,
} from '../../../store/flightLegs/actions';
import appTheme from '../../common/theme/appTheme.json';

export interface FlightDropzoneProps {}
export enum DropzoneState {
  WAITING = 'WAITING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

const baseStyle: React.CSSProperties = {
  display: 'flex',
  height: '211px',
  borderWidth: '2px',
  borderRadius: '16px',
  borderColor: appTheme.primaryTextColor,
  backgroundColor: appTheme.darkGrayTextColor,
  borderStyle: 'dashed',
  color: appTheme.primaryTextColor,
  marginTop: '20px',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
};

const waitingStyle: React.CSSProperties = {
  borderColor: appTheme.mptFormLabelText,
  color: appTheme.mptFormLabelText,
};

const successStyle: React.CSSProperties = {
  borderColor: appTheme.primaryTextColor,
  color: appTheme.primaryTextColor,
};

const failureStyle: React.CSSProperties = {
  borderColor: appTheme.mptPrimaryRed,
  color: appTheme.mptPrimaryRed,
};

const FlightDropzone = (props: FlightDropzoneProps) => {
  const [dropzoneState, setDropzoneState] = useState<DropzoneState>(DropzoneState.WAITING);
  const dispatch = useDispatch();
  const savedMissionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  // successfully parsed flight legs
  const parsedFlightLegs = useSelector(selectSuccessfullyParsedFlightLegs, shallowEqual);
  const failedToParseFlightLegs = useSelector(selectFailedToParseFlightLegs, shallowEqual);

  // Upload file(s) on file drop
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      if (userId && savedMissionPlan.id) {
        dispatch(parseFlightLegFile(savedMissionPlan.id, userId, acceptedFiles));
      }
    },
    [userId, savedMissionPlan.id, dispatch],
  );

  // GET /flightlegs/info whenever file is uploaded
  useEffect(() => {
    if (parsedFlightLegs.length && savedMissionPlan.id && userId) {
      dispatch(getFlightLegsInfo(savedMissionPlan.id, userId));
    }
  }, [parsedFlightLegs, savedMissionPlan.id, userId, dispatch]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections && fileRejections.length > 0) {
      // Dispatch action to update failed to parse flight legs
      dispatch(
        updateFailedToParseFlightLegs(
          fileRejections.map(rejection => ({
            fileName: rejection.file.name,
            error: rejection.errors.map(error => error.message).join(', '),
            flightLeg: undefined,
          })),
        ),
      );
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: '.doc,.docx,.pdf,.kml',
    multiple: true,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(dropzoneState === DropzoneState.WAITING ? waitingStyle : {}),
      ...(dropzoneState === DropzoneState.SUCCESS ? successStyle : {}),
      ...(dropzoneState === DropzoneState.FAILURE ? failureStyle : {}),
      ...(isDragActive ? { borderColor: appTheme.mptFlightLegBlue, color: appTheme.primaryTextColor } : {}),
    }),
    [dropzoneState, isDragActive],
  );

  useEffect(() => {
    if (failedToParseFlightLegs?.length > 0) {
      setDropzoneState(DropzoneState.FAILURE);
    } else if (parsedFlightLegs.length > 0) {
      setDropzoneState(DropzoneState.SUCCESS);
    } else {
      setDropzoneState(DropzoneState.WAITING);
    }
  }, [failedToParseFlightLegs, parsedFlightLegs]);

  const parToDisplay = useMemo(() => {
    if (isDragActive) {
      return <Box>Drop the file(s) here...</Box>;
    }
    switch (dropzoneState) {
      case DropzoneState.FAILURE:
        return (
          <div>
            <Box
              sx={{
                marginTop: '15px',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '20px',
                color: appTheme.primaryTextColor,
              }}
            >
              {failedToParseFlightLegs?.length} file{failedToParseFlightLegs?.length === 1 ? '' : 's'} failed to parse.
              See below for details.
            </Box>
          </div>
        );

      case DropzoneState.SUCCESS:
      default:
        return (
          <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <Box
              sx={{
                marginTop: '15px',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '20px',
                color: appTheme.primaryTextColor,
                marginBottom: '8px',
              }}
            >
              Drag and drop file(s) or click to upload flight plan(s)
            </Box>
            <Box
              sx={{
                width: '284px',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: appTheme.primaryTextColor,
                textAlign: 'center',
              }}
            >
              Supported formats: United and ARINC V9 Word documents, Jeppesen and ACFP PDFs and ForeFlight KMLs.
            </Box>
          </Box>
        );
    }
  }, [dropzoneState, failedToParseFlightLegs, isDragActive]);

  return (
    <div {...getRootProps({ style })}>
      <input {...getInputProps()} />
      <InsertDriveFileRoundedIcon sx={{ color: appTheme.primaryTextColor, fontSize: '41px' }} />
      {parToDisplay}
    </div>
  );
};

export default FlightDropzone;
